import enUS from 'date-fns/locale/en-US';
import { format } from 'date-fns';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';

const timezone = localStorage.getItem('timeZone');
// Once settings are fetched, get the timezone
const userTimezone = timezone || 'America/Chicago';

const FormatCreatedAtDate = ({
  date,
  showDate = true,
  showTime = true,
  showDay = false,
}) => {
  // Format the date based on the user's timezone using Luxon
  const localDate = DateTime.fromISO(date, { zone: 'utc' }).setZone(
    userTimezone,
  );
  const DateFormat = showDay ? 'EEE, MMM dd, yyyy' : 'MMM dd, yyyy';
  const formattedDate = showDate ? localDate.toFormat(DateFormat) : '';
  const formattedTime = showTime ? localDate.toFormat('hh:mm a') : '';

  return (
    <span>
      {formattedDate}
      {formattedDate && formattedTime ? ' ' : ''}
      {formattedTime}
    </span>
  );
};

export default FormatCreatedAtDate;

FormatCreatedAtDate.propTypes = {
  date: PropTypes.string,
  showDate: PropTypes.bool,
  showTime: PropTypes.bool,
  showDay: PropTypes.bool,
};

export const formatDateTimeRange = (startDateValue, startTime) => {
  const formattedStartDate =
    startDateValue &&
    format(startDateValue, 'EEE, MMM dd, yyyy', {
      locale: enUS,
    });

  // // Format startTime as 'hh:mm a' (12-hour clock with AM/PM)
  const formattedStartTime =
    startTime &&
    format(new Date(`1970-01-01T${startTime}`), 'hh:mm a', { locale: enUS });

  return (
    <div>
      {formattedStartDate} {formattedStartTime ? ` ${formattedStartTime}` : ''}
    </div>
  );
};

export const formatDateAndStartEndTimeRange = (
  startDateValue,
  startTime,
  endTime,
) => {
  const formattedStartDate =
    startDateValue &&
    format(startDateValue, 'EEE, MMM dd, yyyy', {
      locale: enUS,
    });

  const formattedStartTime =
    startTime &&
    format(new Date(`1970-01-01T${startTime}`), 'hh:mm a', { locale: enUS });

  const formattedEndTime =
    endTime &&
    format(new Date(`1970-01-01T${endTime}`), 'hh:mm a', { locale: enUS });
  return (
    <div>
      {formattedStartDate}
      <br />
      {formattedStartTime && formattedEndTime
        ? `${formattedStartTime} - ${formattedEndTime}`
        : formattedStartTime || formattedEndTime}
    </div>
  );
};

export const formatStartDate = (startDate) => {
  let formattedDate = new Date(`${startDate?.substring(0, 10)}T12:01:00`);
  const optionsDay = {
    weekday: 'short',
  };

  const day = new Date(formattedDate).toLocaleDateString(undefined, optionsDay);
  const date = format(formattedDate, 'MMM dd, yyyy');
  return { day, date };
};

export const timeZoneDate = (startDateTimeString) => {
  // Parse start date (assumed already in the correct timezone)
  const startDate = DateTime.fromISO(startDateTimeString, {
    zone: userTimezone,
  });

  // Get `now` using user's timezone
  const todayDate = DateTime.now().setZone(userTimezone);

  return { startDate, todayDate };
};

export const timeZoneDateWithAll = (dateTimeString) => {
  // Parse start date (assumed already in the correct timezone)
  const startDate = DateTime.fromISO(dateTimeString);
  return startDate.toJSDate();
};

export const formatDateAsYYYYMMDD = (dateTimeString) => {
  if (!dateTimeString) return null;

  let startDate;

  // Try ISO first
  startDate = DateTime.fromISO(dateTimeString, { zone: 'utc' });
  if (!startDate.isValid) {
    // Try RFC2822 (e.g., "Sat, 17 May 2025 17:01:00 GMT")
    startDate = DateTime.fromRFC2822(dateTimeString, { zone: 'utc' });
  }

  if (!startDate.isValid) {
    console.error('Invalid date passed to formatDateAsYYYYMMDD:', dateTimeString);
    return '';
  }

  return startDate.setZone(userTimezone).toFormat('yyyy-MM-dd');
};
export const IsDateFormatString = async dateString => {
  let dateOfBirth = null;

  if (dateString instanceof Date) {
    // Convert Date object to ISO string and slice
    const isoString = dateString.toISOString(); // "2017-02-11T00:00:00.000Z"
    dateOfBirth = `${isoString.split('T')[0]}T12:01:00`;
  } else if (typeof dateString === 'string') {
    // Already a string
    dateOfBirth = `${dateString.split('T')[0]}T12:01:00`;
  }
  return dateOfBirth;
};

export const formatTime = (timeString) => {
  if (!timeString) {
    return '';
  }
  const [hours, minutes] = timeString.split(':');
  const parsedHours = parseInt(hours, 10);
  const amPmIndicator = parsedHours >= 12 ? 'pm' : 'am';
  const formattedHours = parsedHours % 12 || 12;
  return `${formattedHours}:${minutes} ${amPmIndicator}`;
};
